import React, { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { PostHogErrorBoundary } from 'posthog-js/react';
import Bugsnag from '@bugsnag/js';
import { BugsnagPluginReactResult } from '@bugsnag/plugin-react';
import { Error as ErrorComponent } from './Error';
import './style.scss';
import {
  initializeLegacyBugsnag,
  getErrorReferenceIds,
  isV4Route,
  POSTHOG_APP_NAME,
  sanitizeErrorMetadata,
  sanitizeErrorSearch,
  emitExceptionReplayTriggerEvent,
  getErrorMetadataProperties,
} from '../../utils/errorReporting';

interface Props {
  children?: ReactNode;
}

const ErrorBoundaryFallback = () => (
  <div className="Error__boundaryContainer">
    <div className="Error__boundary">
      <ErrorComponent />
    </div>
  </div>
);

const PostHogV4ErrorBoundary = ({ children }: Props) => (
  <PostHogErrorBoundary
    fallback={<ErrorBoundaryFallback />}
    additionalProperties={(error) => {
      const context = 'React error boundary';
      const replayTriggerEventEmitted =
        emitExceptionReplayTriggerEvent(context);

      return sanitizeErrorMetadata({
        app: POSTHOG_APP_NAME,
        app_name: POSTHOG_APP_NAME,
        context,
        environment: process.env.REACT_APP_ENVIRONMENT,
        mode: process.env.REACT_APP_MODE,
        path: window.location.pathname,
        search: sanitizeErrorSearch(),
        replayTriggerEventEmitted,
        ...getErrorReferenceIds(),
        ...getErrorMetadataProperties(),
        error,
      });
    }}
  >
    {children}
  </PostHogErrorBoundary>
);

let LegacyBugsnagBoundary: any;

const getLegacyBugsnagBoundary = () => {
  if (LegacyBugsnagBoundary) return LegacyBugsnagBoundary;

  initializeLegacyBugsnag();

  const plugin = Bugsnag.getPlugin('react') as
    | BugsnagPluginReactResult
    | undefined;

  LegacyBugsnagBoundary = plugin?.createErrorBoundary(React);
  return LegacyBugsnagBoundary;
};

const BugsnagLegacyErrorBoundary = ({ children }: Props) => {
  const BugsnagBoundary = getLegacyBugsnagBoundary();

  return BugsnagBoundary ? (
    <BugsnagBoundary FallbackComponent={ErrorBoundaryFallback}>
      {children}
    </BugsnagBoundary>
  ) : (
    <>{children}</>
  );
};

export const ErrorBoundary = ({ children }: Props) => {
  useLocation();

  return isV4Route() ? (
    <PostHogV4ErrorBoundary>{children}</PostHogV4ErrorBoundary>
  ) : (
    <BugsnagLegacyErrorBoundary>{children}</BugsnagLegacyErrorBoundary>
  );
};

const ErrorBoundaryHandler = ErrorBoundary;

export default ErrorBoundaryHandler;
